import { ChangeEvent, useEffect, useState } from "react";
import { Datepicker, locale } from "@mobiscroll/react";
import { Box, TextField, toggleButtonGroupClasses, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { CheckinAndMaxAdvancedBooking, MetaData } from "../../Login/typings/login.types";
import { StyledToggleButtonGroup } from "../../../components/Reports/CapacityFilter/DateAndTypeSelector/partial/StyledToggleButtonGroup.partial";
import { StyledToggleButton } from "../../../components/Reports/CapacityFilter/DateAndTypeSelector/partial/StyledToggleButton.partial";
import { WeekdayNames } from "../../../components/FacilityManager/Domain/Types/FloorPlan/WeekdayNames.type";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";

export function MaximumAdvancedBookingPerTypeWeekdaySelect({
  "data-testid": testId,
  setting,
  newValues,
  handleWeekdaySelect,
  handleTimeSelect,
  handleAllowDaysSelect
}: Readonly<{
  "data-testid": string;
  setting: CheckinAndMaxAdvancedBooking;
  newValues: MetaData | undefined;
  handleWeekdaySelect: (w: WeekdayNames | null) => void;
  handleTimeSelect: (t: string) => void;
  handleAllowDaysSelect: (t: number | null) => void;
}>) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { timeFormat } = useTimeFormat();

  const [newToggles, setNewToggles] = useState<string[] | null>([]);
  const [newTime, setNewTime] = useState("");
  const [newAllowDays, setNewAllowDays] = useState<number | null>(null);

  // All weekday enum values
  const weekdayList = Object.values(WeekdayNames);

  function handleWeekdayToggle(event: any, value: string | null) {
    const newVal = (value as WeekdayNames) ?? null;

    // remove selection
    if (value === newToggles?.[0]) {
      setNewToggles([]);
      handleWeekdaySelect(null);
      return;
    }

    setNewToggles([newVal]);
    handleWeekdaySelect(newVal);
  }

  function handleTimeChange(value: string) {
    setNewTime(value);
    handleTimeSelect(value);
  }

  const updateAllowDays = (value: number | null) => {
    setNewAllowDays(value);
    handleAllowDaysSelect(value);
  };

  function handleAllowDaysChange(ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const val = ev.target.value;
    const value = Number(val);

    // Allow clearing the field
    if (val === "") return updateAllowDays(null);
    // Enforce minimum
    if (value < 0) return updateAllowDays(0);

    updateAllowDays(value);
  }

  useEffect(() => {
    // initially set weekdays toggle
    setNewToggles(setting.weekday ? [setting.weekday] : []);
    setNewTime(setting.time ?? "");
    setNewAllowDays(setting.allowedBookingDaysInAdvance ?? null);
  }, []);

  return (
    <Box data-testid={testId} sx={{ pb: 0.5 }}>
      <StyledToggleButtonGroup
        value={newToggles}
        onChange={handleWeekdayToggle}
        data-testid={`companyCard-maxAdvancedBookingPerType-weekday-toggle-group-btn`}
        orientation={"horizontal"}
        exclusive
        sx={{
          flexWrap: "wrap",
          [`& .${toggleButtonGroupClasses.grouped}`]: {
            justifyContent: "center",
            backgrondColor: theme.palette.background.paper
          },
          [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]:
            {}
        }}
      >
        {weekdayList.map(day => {
          const isSaturdayBlocked = !newValues?.bookingsOnSaturdaysAllowed;
          const isSundayBlocked = !newValues?.bookingsOnSundaysAllowed;

          if (day === WeekdayNames.SATURDAY && isSaturdayBlocked) return null;
          if (day === WeekdayNames.SUNDAY && isSundayBlocked) return null;

          return (
            <StyledToggleButton
              key={day}
              disableRipple
              value={day}
              selected={newToggles?.includes(day)}
              data-testid={`companyCard-maxAdvancedBookingPerType-weekday-${day}-toggle-btn`}
              sx={{
                width: 57.5,
                border: `0.2px solid ${
                  theme.palette.mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255,255,255,0.12)"
                }`,
                fontSize: "0.85rem",
                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.main,
                  "&:hover": { backgroundColor: theme.palette.primary.main }
                }
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>{t(day.slice(0, 3))}</Box>
            </StyledToggleButton>
          );
        })}
      </StyledToggleButtonGroup>

      <Box
        sx={{ display: "flex", alignItems: "center", flexDirection: isMobile ? "column" : "row" }}
      >
        <Datepicker
          controls={["time"]}
          data-testid={"maxAdvancedBookingPerType-time-picker"}
          timeFormat={timeFormat}
          stepMinute={15}
          label={t("Time")}
          buttons={[{ text: t("Clear"), handler: () => handleTimeChange("") }]}
          onChange={ev => handleTimeChange(DateTime.fromJSDate(ev.value as Date).toFormat("HH:mm"))}
          value={newTime}
          locale={locale[i18n.language]}
        />
        <TextField
          id="maxAdvancedBookingPerType-allowDays-input"
          data-testid={"maxAdvancedBookingPerType-allowDays-input"}
          sx={{ m: "1.5em 1em", pt: 1 }}
          value={newAllowDays ?? ""}
          label={t("_inputAllowedBookingDaysInAdvance")} // allowedBookingDaysInAdvance
          type={"number"}
          onChange={handleAllowDaysChange}
        />
      </Box>
    </Box>
  );
}
